import React from "react";
import type { DetailData } from "../../../../../contexts/chat-log/chat-log-context";
import { useChatbotConfig } from "../../../../../contexts/chatbot-config";
import { isDirectNavigationLink } from "../../../../../libs/fns";
import AssetCard from "./asset-card";

export default function ImageDataset({
  file_path,
  file_name,
  link,
  asset_title,
  text,
  fullWidth,
}: Pick<DetailData, "file_path" | "file_name" | "link" | "asset_title" | "text"> & { fullWidth?: boolean }) {
  const [images, setImages] = React.useState<string[]>([]);
  // 拡大ポップアップ表示中の画像URL
  const [lightboxUrl, setLightboxUrl] = React.useState<string | null>(null);
  const { chatbotData } = useChatbotConfig();
  const baseUrl = chatbotData?.chatbot_S3_URL || "";

  // file_pathをカンマで分割して画像配列を生成
  React.useEffect(() => {
    const paths = file_path.split(",").map((p) => p.trim()).filter((p) => p);
    const fullPaths = paths.map((path) => {
      if (path && !path.startsWith("http")) {
        return `${baseUrl}/${path}`;
      }
      return path;
    });
    setImages(fullPaths);
  }, [file_path, baseUrl]);

  const handleClick = (imagePath: string) => {
    if (link && isDirectNavigationLink(link)) {
      window.location.href = link;
    } else if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      // リンクがない場合はポップアップで拡大表示
      setLightboxUrl(imagePath);
    }
  };

  // ESCキーでポップアップを閉じる
  React.useEffect(() => {
    if (!lightboxUrl) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxUrl(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxUrl]);

  return (
    <>
      {/* 拡大ポップアップ */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              border: "2px solid rgba(255,255,255,0.6)",
              color: "#fff",
              fontSize: "18px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              padding: 0,
              backdropFilter: "blur(4px)",
            }}
            aria-label="閉じる"
          >
            ×
          </button>
          <img
            src={lightboxUrl}
            alt={file_name}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: "12px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
            }}
          />
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexShrink: 0,
          gap: "8px",
          overflowX: "auto",
          padding: "4px 0",
        }}
      >
        {images.map((path, index) => (
          <AssetCard
            key={index}
            thumbnailUrl={path}
            alt={`${file_name} - ${index + 1}`}
            title={asset_title || undefined}
            description={text || undefined}
            onButtonClick={() => handleClick(path)}
            fullWidth={fullWidth}
          />
        ))}
      </div>
    </>
  );
}
